import Typography from '@mui/material/Typography';
import { SelectableTable } from '@openx/components-table/SelectableTable/SelectableTable.tsx';
import { Table, TableName, type TableProps } from '@openx/components-table/Table.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import type { Deal, DealsListFiltersValue, ListDealBase } from '@openx/types/deal.ts';
import type { ToggleSelect } from '@openx/types/selectableTable.ts';
import type { AllTableCriteria, ChangeCriteriaHandler } from '@openx/types/tableCriteria.ts';
import type { RowAction, TableColumn } from '@openx/types/tableHelpers.ts';
import { parseMultiValueFilter } from '@openx/utils/lib/multiValueFilter.ts';
import { sortMenuButtonItems } from '@openx/utils/lib/tableHelpers.ts';
import { noop } from '@openx/utils/noop.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DealsRowsActionsType } from '../utils/context/DealHookTypes.ts';
import { DealIdGroupCreateButton } from './parts/DealIdGroupCreateButton.tsx';

const DealsSelectableTable = <T extends Partial<Deal>>() => SelectableTable<T>(Table);

export interface DealsTableProps<T extends ListDealBase> {
  onCriteriaChange: ChangeCriteriaHandler;
  toggleSelect?: ToggleSelect<T>;
  hideActions?: boolean;
  hideToggleSelect?: boolean;
  columns: TableColumn<T>[];
  useDealsRowActions: DealsRowsActionsType;
  topManagementSection?: TableProps<T>['topManagementSection'];
  loading?: boolean;
  selectedData?: T[];
  dealsList: T[];
  criteria: AllTableCriteria;
  onRowClick?: RowAction<T>;
}

export const DealsTable = <T extends ListDealBase>({
  onCriteriaChange,
  toggleSelect = noop,
  hideActions,
  hideToggleSelect,
  columns,
  topManagementSection,
  dealsList,
  loading = false,
  selectedData = [],
  useDealsRowActions,
  criteria,
  onRowClick,
}: DealsTableProps<T>) => {
  const criteriaFilters = criteria.filters as DealsListFiltersValue;
  const { t } = useTranslation();
  const { useIsAllowed, isOa } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const isInternal = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));
  const shouldDisplayDealIdGroupButton = isInternal || isOa;

  const filteredDealIds = parseMultiValueFilter(criteriaFilters?.deal_id || criteriaFilters.exchange_deal_id);
  const filteredDealId = filteredDealIds.length === 1 ? filteredDealIds[0] : undefined;
  const actions = useDealsRowActions();
  const rowActions = useMemo(() => {
    const excludedActions =
      actions.RESUME !== undefined && actions.PAUSE !== undefined
        ? [actions.TROUBLESHOOT, actions.CHANGE_STATUS]
        : [actions.TROUBLESHOOT];

    return hideActions ? undefined : sortMenuButtonItems(actions, excludedActions);
  }, [actions, hideActions]);
  const DealsTable = useMemo(() => (hideToggleSelect ? Table<T> : DealsSelectableTable<T>()), [hideToggleSelect]);

  return (
    <DealsTable
      name={TableName.DEALS}
      data={dealsList}
      loading={loading}
      columns={columns}
      rowActions={rowActions}
      primaryRowAction={actions.TROUBLESHOOT}
      secondaryRowAction={actions.CHANGE_STATUS}
      onCriteriaChange={onCriteriaChange}
      onRowClick={onRowClick}
      criteria={criteria}
      toggleSelect={toggleSelect}
      selectedItems={selectedData}
      topManagementSection={topManagementSection}
      customManagementSection={
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t(
            'All metrics show values for the last 7 days. Deltas reflect the change compared to the previous 7-day period.'
          )}
        </Typography>
      }
      loadingOnInit
      groupBy={filteredDealId ? 'deal_id' : undefined}
      forceExpandGroupedItems
      groupedItemsActions={shouldDisplayDealIdGroupButton && <DealIdGroupCreateButton dealId={filteredDealId} />}
      stickyActionColumn
    />
  );
};
